// #3989 (offline Phase 2, umbrella F4) — pure decision rules for the image
// upload outbox. Kept free of Angular/PouchDB imports so the give-up policy is
// unit-testable under mocha/ts-node (see image-upload-outbox-rules.test.ts),
// the same split as sign-out-guard.ts vs sign-out-guard-flow.ts.

// Give-up thresholds. An item that keeps failing forever would retry on every
// reconnect and every login for the rest of time; cap it and surface the drop
// via telemetry instead (trackSilentProblem 'ImageOutboxItemDropped' +
// Mixpanel 'image_outbox_dropped' — see image-upload-outbox.service.ts).
export const OUTBOX_MAX_ATTEMPTS = 10;
export const OUTBOX_MAX_AGE_DAYS = 14;

/** Decide whether a queued image upload should be abandoned. `attempts` counts
 * only drain attempts that ran while the reachability probe said Online — an
 * offline drain skip does not burn an attempt, so a long offline stretch
 * cannot exhaust the cap by itself. */
export function shouldDropOutboxItem(input: { attempts: number; ageDays: number }): boolean {
  return input.attempts >= OUTBOX_MAX_ATTEMPTS || input.ageDays > OUTBOX_MAX_AGE_DAYS;
}

/** Rebuild image bytes from the outbox doc's `bytesBase64` field (base64
 * WITHOUT a data-URL prefix). Lives here — not in the service — because it is
 * on the photo data-integrity path and must stay unit-testable under
 * mocha/ts-node (atob and Blob exist in modern Node). The encode direction
 * (blobToBase64Async) needs the browser-only FileReader and stays in the
 * service. */
export function base64ToBlob(base64: string, contentType: string): Blob {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

/**
 * Whether a failed drain attempt means the object is already in the bucket
 * under this item's key, so the item can never upload and must stop retrying.
 *
 * Reached when the server answers a write-URL request with
 * `STORAGE_WRITE_OBJECT_EXISTS_HTTP_STATUS` (#5016). For a key minted before
 * owner tokens existed the server cannot say who owns the object, so it refuses
 * rather than let one caller overwrite another's.
 *
 * **This deliberately does not try to work out WHOSE object it is.** Comparing
 * the stored object against the bytes still queued here was built first and is
 * unworkable on the web build: every queued item's own bytes are written into
 * the service worker's `images` cache at enqueue time
 * (`ImageService.precacheQueuedImageAsync`), under exactly the public URL the
 * comparison would read, and the worker serves that cache first. So the
 * comparison reads the item's own bytes back and answers "mine" whatever is
 * really in the bucket — which would then discard the queued bytes while the
 * document points at somebody else's object. That is worse than not checking:
 * silent data loss on the one path the check existed to protect.
 */
export function isObjectAlreadyStoredError(err: unknown, alreadyStoredHttpStatus: number): boolean {
  return (err as { httpStatus?: number })?.httpStatus === alreadyStoredHttpStatus;
}

/**
 * Whether a failed drain attempt is reported as ImageOutboxPutRejected
 * (df-28f906a7558b). The PUT is marked as caller-reported, so the global fetch
 * wrapper no longer files a rejected PUT as a Fetch error; the drain reports
 * the one case the wrapper used to report that nothing else does: the PUT
 * itself was rejected (not an HTTP error status, not a step before or after
 * it), the rejection is not a recognised connection blip, and the online check
 * did not find the device offline. A blip stays queued with no report, an HTTP
 * error status only counts toward the give-up rule, as before.
 */
export function shouldReportOutboxPutRejection(input: { putRejected: boolean; transient: boolean; foundOffline: boolean }): boolean {
  return input.putRejected && !input.transient && !input.foundOffline;
}
